import { Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useMemo, useState } from 'react';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import {
  LastStepVM,
  ParentVM,
  PublicationVM,
  RiferimentiPubblicazione,
  SectionVM,
} from 'civil-status/view-models/step-form';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import FilterPublication from './filter';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker, TimePicker } from '@mui/lab';
import { ResponsePublication } from 'civil-status/model/publication';
import ErrorModal from 'shell/components/shared/modal-error';
import { transformPublicationResponse } from 'civil-status/utils/transform-publication-response';
import { initialFilterPublication } from 'civil-status/constants/initial-values-step';
import { PmiButton } from 'shell/components/shared/button';
import { isObjectEmpty } from 'shell/utils/is-object-empty';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { DateTime } from 'luxon';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';
import { AccordionGeneralityForm } from 'civil-status/view-models/accordion-generality-form';
import { toast } from 'react-toastify';
import { ID_PUBBLICATIONS } from '../list/list-publication';
import { ID_GENDERS } from 'shell/components/shared/gender';

interface PublicationDataProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  idEvento?: number;
}

const PublicationDataComponent = ({
  formContext,
  disabled = false,
  values,
  onChange,
  onChanges,
  idEvento,
}: PublicationDataProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const [openModalError, setOpenModalError] = useState<{ open?: boolean; message?: string }>({
    open: false,
    message: '',
  });

  const valuesPublication: PublicationVM = useMemo(() => {
    return values?.publicationData || {};
  }, [values]);

  const valuesFilterPublication: PublicationVM = useMemo(() => {
    return values?.filterPublication || {};
  }, [values]);

  const disabledAcquisition: boolean = useMemo(() => {
    return isObjectEmpty(valuesPublication as Record<string, unknown>);
  }, [valuesPublication]);

  const getData = useHttpClient<HttpResponse<ResponsePublication>, ResponsePublication>(
    `${__ACT_API_URL__}/pubblicazioni-matrimonio`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const onSearch = useEventCallback(() => {
    const data = {
      anno: valuesFilterPublication.year || valuesFilterPublication.yearSurname || undefined,
      numero: valuesFilterPublication.number || undefined,
      parte: valuesFilterPublication.part || undefined,
      serie: valuesFilterPublication.serie || undefined,
      registro: valuesFilterPublication.register || undefined,
      cognomeSposo: valuesFilterPublication.surnameGroom || undefined,
      cognomeSposa: valuesFilterPublication.surnameWife || undefined,
    };

    (
      getData({
        data,
      }) as ResponsePromise<HttpResponse<ResponsePublication>>
    ).then((response) => {
      // chiusura accordion dei filtri
      setOpen(false);

      const castedResponse = response?.data.payload || {};

      if (castedResponse.messaggioErrore) {
        // presente errore, mostrare modale

        setOpenModalError({
          open: true,
          message: castedResponse.messaggioErrore || '',
        });

        return;
      }

      const transformResponse: PublicationVM | undefined =
        transformPublicationResponse(castedResponse);

      onChanges({
        publicationData: transformResponse || initialFilterPublication,
        filterPublication: initialFilterPublication,
      });
    });
  });

  const onChangePublication = useEventCallback(
    (key: keyof PublicationVM, value: PublicationVM[keyof PublicationVM]) => {
      onChange('publicationData', {
        ...(values || {}).publicationData,
        [key]: value,
      });
    }
  );

  const onChangesPublication = useEventCallback((valuesNew: PublicationVM) => {
    onChange('publicationData', {
      ...valuesNew,
    });
  });

  const onChangeFilter = useEventCallback(
    (key: keyof PublicationVM, value: PublicationVM[keyof PublicationVM]) => {
      onChange('filterPublication', {
        ...valuesFilterPublication,
        [key]: value,
      });
    }
  );

  const onChangesFilter = useEventCallback((valuesNew: PublicationVM) => {
    onChange('filterPublication', {
      ...(valuesNew || {}),
    });
  });

  const saveKey = useHttpClient<HttpResponse<unknown>, unknown>(
    `${__ACT_API_URL__}/riferimenti-pubblicazione/save`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const onAcquisitionData = useEventCallback(() => {
    const data: RiferimentiPubblicazione = {
      key: {
        idEvento,
      },
      numeroAnno: valuesPublication.year ? Number.parseInt(valuesPublication.year) : undefined,
      numeroAtto: valuesPublication.number ? Number.parseInt(valuesPublication.number) : undefined,
      codiceParte: valuesPublication.part || undefined,
      codiceSerie: valuesPublication.serie || undefined,
      codiceRegistro: valuesPublication.register || undefined,
    };

    (saveKey({ data }) as ResponsePromise<void>).then(() => {
      formContext.setValues({
        ...formContext.values,
        [KeySections.keyDatiSpecificiMatr]: {
          ...(formContext.values[KeySections.keyDatiSpecificiMatr] || {}),
          publication:
            valuesPublication.response?.tipoDic === '0'
              ? ID_PUBBLICATIONS.ID_PUBBLICATION_SINGLE
              : valuesPublication.response?.tipoDic === '1' ||
                valuesPublication.response?.tipoDic === '2'
              ? ID_PUBBLICATIONS.ID_PUBBLICATION_OCCURRED
              : '',
        } as SectionVM,
        [KeySections.keyPubblicazione1]: {
          ...(formContext.values[KeySections.keyPubblicazione1] || {}),
          isRequiredFromOtherAccordion:
            valuesPublication.reducedDay?.toString() && valuesPublication.reducedDay !== 0,
          isReductionTerms: valuesPublication.reducedDay && valuesPublication.reducedDay < 8,
          dataStart: valuesPublication.response?.dataInizio
            ? DateTime.fromFormat(valuesPublication.response?.dataInizio, 'dd/MM/yyyy')
            : null,
          dataEnd: valuesPublication.response?.dataFine
            ? DateTime.fromFormat(valuesPublication.response?.dataFine, 'dd/MM/yyyy')
            : null,
          isForeign: false,
          italian: {
            municipality: MUNICIPALITY_MILANO,
          },
        } as SectionVM,
        [KeySubjects.keySposo]: {
          ...(formContext.values[KeySubjects.keySposo] || {}),
          searchPerson: {
            name: valuesPublication.response?.nomeSposo,
            surname: valuesPublication.response?.cognomeSposo,
            birthdayDate: valuesPublication.response?.dataNascitaSposo
              ? DateTime.fromFormat(valuesPublication.response?.dataNascitaSposo, 'dd/MM/yyyy')
              : null,
            birthdayDateChoice: valuesPublication.response?.dataNascitaSposo
              ? ['day', 'month', 'year']
              : undefined,
          },
          generality: {
            idSubject: (formContext.values[KeySubjects.keySposo] || {}).generality?.idSubject,
            name: valuesPublication.response?.nomeSposo,
            surname: valuesPublication.response?.cognomeSposo,
            birthDate: valuesPublication.response?.dataNascitaSposo
              ? DateTime.fromFormat(valuesPublication.response?.dataNascitaSposo, 'dd/MM/yyyy')
              : null,
            gender: ID_GENDERS.MASCHIO,
          } as AccordionGeneralityForm,
        } as ParentVM,
        [KeySubjects.keySposa]: {
          ...(formContext.values[KeySubjects.keySposa] || {}),
          searchPerson: {
            name: valuesPublication.response?.nomeSposa,
            surname: valuesPublication.response?.cognomeSposa,
            birthdayDate: valuesPublication.response?.dataNascitaSposa
              ? DateTime.fromFormat(valuesPublication.response?.dataNascitaSposa, 'dd/MM/yyyy')
              : null,
            birthdayDateChoice: valuesPublication.response?.dataNascitaSposa
              ? ['day', 'month', 'year']
              : undefined,
          },
          generality: {
            idSubject: (formContext.values[KeySubjects.keySposa] || {}).generality?.idSubject,
            name: valuesPublication.response?.nomeSposa,
            surname: valuesPublication.response?.cognomeSposa,
            birthDate: valuesPublication.response?.dataNascitaSposa
              ? DateTime.fromFormat(valuesPublication.response?.dataNascitaSposa, 'dd/MM/yyyy')
              : null,
            gender: ID_GENDERS.FEMMINA,
          } as AccordionGeneralityForm,
        } as ParentVM,
      });

      toast.success('Operazione eseguita con successo');
    });
  });

  return (
    <Fragment>
      <Grid container spacing={2} item>
        <Grid item xs={12}>
          <FilterPublication
            onChange={onChangeFilter}
            onChanges={onChangesFilter}
            setOpen={setOpen}
            open={open}
            values={valuesFilterPublication}
            disabled={disabled}
            onSearch={onSearch}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="year"
            label="Anno"
            aria-label="Anno"
            name="year"
            fullWidth
            onChange={(event) => onChangePublication('year', event.target.value)}
            value={valuesPublication?.year}
            type="number"
            inputProps={{
              maxLength: '4',
            }}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="number"
            label="Numero"
            aria-label="Numero"
            name="number"
            fullWidth
            onChange={(event) => onChangePublication('number', event.target.value)}
            value={valuesPublication?.number}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <PmiTextField
            id="part"
            label="Parte"
            aria-label="Parte"
            name="part"
            fullWidth
            onChange={(event) => onChangePublication('part', event.target.value)}
            value={valuesPublication?.part}
            inputProps={{
              maxLength: '1',
            }}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="serie"
            label="Serie"
            aria-label="Serie"
            name="serie"
            fullWidth
            onChange={(event) => onChangePublication('serie', event.target.value)}
            value={valuesPublication?.serie}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="register"
            label="Registro"
            aria-label="Registro"
            name="register"
            fullWidth
            onChange={(event) => onChangePublication('register', event.target.value)}
            value={valuesPublication?.register}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            id="state"
            label="Stato pubblicazione"
            aria-label="Stato pubblicazione"
            name="state"
            fullWidth
            onChange={(event) => onChangePublication('state', event.target.value)}
            value={valuesPublication?.state}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data richiesta"
            aria-label="Data richiesta"
            value={valuesPublication?.date || null}
            onChange={(date) => {
              onChangePublication('date', date);
            }}
            disabled
            renderInput={(params) => <PmiTextField {...params} id="date" fullWidth helperText="" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TimePicker
            label="Ora richiesta"
            value={valuesPublication?.time || null}
            disabled
            onChange={(value) => onChangePublication('time', value)}
            renderInput={(params) => (
              <PmiTextField id="time" name="time" {...params} fullWidth helperText="" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiTextField
            id="residence"
            label="Residenza sposi"
            aria-label="Residenza sposi"
            name="residence"
            fullWidth
            onChange={(event) => onChangePublication('residence', event.target.value)}
            value={valuesPublication?.residence}
            disabled
          />
        </Grid>
        {valuesPublication.reducedDay && valuesPublication.reducedDay < 8 ? (
          <Grid item xs={12}>
            PUBBLICAZIONE CON TERMINE RIDOTTO: GIORNI {valuesPublication.reducedDay}
          </Grid>
        ) : undefined}
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Sposo"
            aria-label="Sposo"
            id="nameSurnameGroom"
            name="nameSurnameGroom"
            fullWidth
            type="text"
            disabled
            variant="outlined"
            value={valuesPublication?.nameSurnameGroom || ''}
            onChange={(event) => onChangePublication('nameSurnameGroom', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Sposa"
            aria-label="Sposa"
            id="nameSurnameWife"
            name="nameSurnameWife"
            fullWidth
            type="text"
            disabled
            variant="outlined"
            value={valuesPublication?.nameSurnameWife || ''}
            onChange={(event) => onChangePublication('nameSurnameWife', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="center">
          <PmiButton
            color="cdmBrand"
            variant="outlined"
            onClick={() => {
              onChangesPublication(initialFilterPublication);
            }}
            disabled={disabled}
          >
            Pulisci
          </PmiButton>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={onAcquisitionData}
            disabled={disabled || disabledAcquisition}
            style={{ marginLeft: '1rem' }}
          >
            ACQUISISCI DATI
          </PmiButton>
        </Grid>
      </Grid>
      <ErrorModal
        message={openModalError.message || ''}
        open={openModalError.open || false}
        onClose={() => {
          setOpenModalError({
            open: false,
            message: '',
          });
        }}
      />
    </Fragment>
  );
};

PublicationDataComponent.displayName = 'Form Pubblication Data component';

const PublicationData = memo(PublicationDataComponent);

export default PublicationData;
